import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFrameworksApi, adminMappingsApi } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { isPlatformAdmin } from '../utils/adminAccess';
import { useToast } from '../hooks/useToast';
import { PageHeader } from '../components/PageHeader';
import { usePagination } from '../hooks/usePagination';
import { Pagination } from '../components/Pagination';
import { NimbusTable } from '../components/NimbusTable';

type FrameworkControl = {
  controlId: string;
  title: string;
  description?: string;
};

type Framework = {
  frameworkId: string;
  name: string;
  controls?: FrameworkControl[];
};

type Mapping = {
  sourceFrameworkId: string;
  targetFrameworkId: string;
  sourceControlId: string;
  sourceControlTitle?: string;
  targetControlId: string;
  targetControlTitle?: string;
  mappingType: 'equivalent' | 'partial' | 'related' | string;
  notes?: string;
};

export const AdminMappingsPage: React.FC = () => {
  const { user } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const isAdmin = isPlatformAdmin(user?.email || '');

  const { data: frameworks = [], isLoading, error } = useQuery<Framework[]>({
    queryKey: ['adminFrameworks'],
    queryFn: () => adminFrameworksApi.getAll(),
    enabled: !!user && isAdmin,
  });

  const [sourceFrameworkId, setSourceFrameworkId] = useState('');
  const [targetFrameworkId, setTargetFrameworkId] = useState('');

  const sourceFramework = useMemo(
    () => frameworks.find((f) => f.frameworkId === sourceFrameworkId) || null,
    [frameworks, sourceFrameworkId]
  );
  const targetFramework = useMemo(
    () => frameworks.find((f) => f.frameworkId === targetFrameworkId) || null,
    [frameworks, targetFrameworkId]
  );

  const [currentMappings, setCurrentMappings] = useState<Mapping[]>([]);
  const [pendingMappings, setPendingMappings] = useState<Mapping[]>([]);

  const [selectedSourceControlId, setSelectedSourceControlId] = useState<string | null>(null);
  const selectedSourceControl = useMemo(
    () => (sourceFramework?.controls || []).find((c) => c.controlId === selectedSourceControlId) || null,
    [sourceFramework, selectedSourceControlId]
  );

  const [selectedTargetControlId, setSelectedTargetControlId] = useState<string | null>(null);
  const selectedTargetControl = useMemo(
    () => (targetFramework?.controls || []).find((c) => c.controlId === selectedTargetControlId) || null,
    [targetFramework, selectedTargetControlId]
  );

  const [mappingType, setMappingType] = useState<'equivalent' | 'partial' | 'related'>('equivalent');
  const [notes, setNotes] = useState('');

  const [sourceSearch, setSourceSearch] = useState('');
  const [targetSearch, setTargetSearch] = useState('');

  const isReady = !!sourceFrameworkId && !!targetFrameworkId;

  useEffect(() => {
    setSelectedSourceControlId(null);
    setSelectedTargetControlId(null);
    setPendingMappings([]);
    setCurrentMappings([]);
    setSourceSearch('');
    setTargetSearch('');
  }, [sourceFrameworkId, targetFrameworkId]);

  const loadMappingsMutation = useMutation({
    mutationFn: async () => {
      if (!sourceFrameworkId || !targetFrameworkId) return [] as Mapping[];
      return adminMappingsApi.list(sourceFrameworkId, targetFrameworkId);
    },
    onSuccess: (mappings) => {
      setCurrentMappings(mappings || []);
    },
  });

  useEffect(() => {
    if (!isReady) return;
    loadMappingsMutation.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady, sourceFrameworkId, targetFrameworkId]);

  const saveBulkMutation = useMutation({
    mutationFn: async () => {
      if (pendingMappings.length === 0) return;
      await adminMappingsApi.bulkCreate(pendingMappings);
    },
    onSuccess: async () => {
      setPendingMappings([]);
      await qc.invalidateQueries({ queryKey: ['adminFrameworks'] });
      if (sourceFrameworkId && targetFrameworkId) {
        const mappings = await adminMappingsApi.list(sourceFrameworkId, targetFrameworkId);
        setCurrentMappings(mappings || []);
      }
    },
  });

  const filteredSourceControls = useMemo(() => {
    const search = sourceSearch.trim().toLowerCase();
    const list = sourceFramework?.controls || [];
    if (!search) return list;
    return list.filter((c) => `${c.controlId} ${c.title} ${c.description || ''}`.toLowerCase().includes(search));
  }, [sourceFramework, sourceSearch]);

  const filteredTargetControls = useMemo(() => {
    const search = targetSearch.trim().toLowerCase();
    const list = targetFramework?.controls || [];
    if (!search) return list;
    return list.filter((c) => `${c.controlId} ${c.title} ${c.description || ''}`.toLowerCase().includes(search));
  }, [targetFramework, targetSearch]);

  const mappedSourceIds = useMemo(() => {
    const s = new Set<string>();
    currentMappings.forEach((m) => s.add(m.sourceControlId));
    pendingMappings.forEach((m) => s.add(m.sourceControlId));
    return s;
  }, [currentMappings, pendingMappings]);

  const totalSource = sourceFramework?.controls?.length || 0;
  const mappedCount = mappedSourceIds.size;

  const { pageItems: pageMappings, page: mappingsPage, setPage: setMappingsPage, totalPages: mappingsTotalPages, pageSize: mappingsPageSize, totalItems: mappingsTotalItems } = usePagination(currentMappings, 25);

  const addMapping = () => {
    if (!selectedSourceControl || !selectedTargetControl) return;

    const mapping: Mapping = {
      sourceFrameworkId,
      targetFrameworkId,
      sourceControlId: selectedSourceControl.controlId,
      sourceControlTitle: selectedSourceControl.title,
      targetControlId: selectedTargetControl.controlId,
      targetControlTitle: selectedTargetControl.title,
      mappingType,
      notes,
    };

    setPendingMappings((p) => [...p, mapping]);
    setCurrentMappings((c) => [...c, mapping]);

    setSelectedTargetControlId(null);
    setMappingType('equivalent');
    setNotes('');
  };

  const removeMapping = (index: number) => {
    setCurrentMappings((cur) => {
      const next = [...cur];
      const removed = next.splice(index, 1)[0];
      if (removed) {
        setPendingMappings((pending) =>
          pending.filter(
            (m) =>
              !(
                m.sourceControlId === removed.sourceControlId &&
                m.targetControlId === removed.targetControlId &&
                m.sourceFrameworkId === removed.sourceFrameworkId &&
                m.targetFrameworkId === removed.targetFrameworkId
              )
          )
        );
      }
      return next;
    });
  };

  if (!isAdmin) {
    return (
      <div className="container-xxl flex-grow-1 container-p-y np-page">
        <div className="np-banner np-tone-warning">
          <i className="ri-error-warning-line"></i>
          <span>Access denied. Admin privileges required.</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-xxl flex-grow-1 container-p-y np-page">
      <PageHeader icon="ri-git-merge-line" title="Framework Mappings" />

      <div className="np-card">
        <div className="np-card-body">
          {isLoading ? (
            <div className="d-flex justify-content-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : error ? (
            <div className="np-banner np-tone-danger">
              <i className="ri-error-warning-line"></i>
              <span>Failed to load frameworks.</span>
            </div>
          ) : (
            <div className="row g-3">
              <div className="col-12 col-md-5">
                <div className="np-field mb-0">
                  <label>Source Framework</label>
                  <select className="np-select" value={sourceFrameworkId} onChange={(e) => setSourceFrameworkId(e.target.value)}>
                    <option value="">Select source framework</option>
                    {frameworks.map((fw) => (
                      <option key={fw.frameworkId} value={fw.frameworkId}>
                        {fw.name} ({fw.controls?.length || 0} controls)
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="col-12 col-md-2 d-flex align-items-end justify-content-center">
                <div style={{ fontSize: '2rem' }}>→</div>
              </div>
              <div className="col-12 col-md-5">
                <div className="np-field mb-0">
                  <label>Target Framework</label>
                  <select className="np-select" value={targetFrameworkId} onChange={(e) => setTargetFrameworkId(e.target.value)}>
                    <option value="">Select target framework</option>
                    {frameworks.map((fw) => (
                      <option key={fw.frameworkId} value={fw.frameworkId}>
                        {fw.name} ({fw.controls?.length || 0} controls)
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="col-12">
                <div className="d-flex align-items-center gap-2">
                  <span className="np-chip np-tone-info">{mappedCount} of {totalSource} controls mapped</span>
                  <button
                    className="np-btn np-btn-primary np-btn-sm"
                    onClick={() => saveBulkMutation.mutate()}
                    disabled={saveBulkMutation.isPending || pendingMappings.length === 0}
                  >
                    {saveBulkMutation.isPending ? 'Saving…' : 'Save Mappings'}
                  </button>
                </div>
                {saveBulkMutation.error ? (
                  <div className="np-banner np-tone-danger mt-3">
                    <i className="ri-error-warning-line"></i>
                    <span>Failed to save mappings.</span>
                  </div>
                ) : null}
              </div>
            </div>
          )}
        </div>
      </div>

      {isReady ? (
        <div className="row g-4">
          <div className="col-12 col-md-6">
            <div className="np-card">
              <div className="np-card-head">
                <div>
                  <h2>Source Controls</h2>
                  <p className="np-sub">{sourceFramework?.name}</p>
                </div>
                <div className="np-search" style={{ width: 220 }}>
                  <i className="ri-search-line"></i>
                  <input
                    placeholder="Search controls..."
                    value={sourceSearch}
                    onChange={(e) => setSourceSearch(e.target.value)}
                  />
                </div>
              </div>
              <div className="np-card-body" style={{ maxHeight: 600, overflowY: 'auto' }}>
                <div className="np-choice-list">
                  {(filteredSourceControls || []).map((c) => {
                    const isSelected = c.controlId === selectedSourceControlId;
                    const isMapped = mappedSourceIds.has(c.controlId);
                    return (
                      <div
                        key={c.controlId}
                        className={`np-choice ${isSelected ? 'selected np-tone-primary' : ''}`}
                        onClick={() => setSelectedSourceControlId(c.controlId)}
                      >
                        <div className="np-choice-body">
                          <code>{c.controlId}</code>
                          <div className="np-choice-label mt-1">{c.title}</div>
                          <p className="np-choice-desc">{c.description || ''}</p>
                        </div>
                        <span className={`np-chip ${isMapped ? 'np-tone-success' : 'np-tone-warning'}`}>{isMapped ? 'Mapped' : 'Unmapped'}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-6">
            <div className="np-card">
              <div className="np-card-head">
                <div>
                  <h2>Target Controls</h2>
                  <p className="np-sub">{targetFramework?.name}</p>
                </div>
                <div className="np-search" style={{ width: 220 }}>
                  <i className="ri-search-line"></i>
                  <input
                    placeholder="Search controls..."
                    value={targetSearch}
                    onChange={(e) => setTargetSearch(e.target.value)}
                  />
                </div>
              </div>
              <div className="np-card-body" style={{ maxHeight: 600, overflowY: 'auto' }}>
                <div className="np-choice-list">
                  {(filteredTargetControls || []).map((c) => {
                    const isSelected = c.controlId === selectedTargetControlId;
                    return (
                      <div
                        key={c.controlId}
                        className={`np-choice ${isSelected ? 'selected np-tone-primary' : ''}`}
                        onClick={() => {
                          if (!selectedSourceControlId) {
                            toast.warning('Please select a source control first');
                            return;
                          }
                          setSelectedTargetControlId(c.controlId);
                        }}
                      >
                        <div className="np-choice-body">
                          <code>{c.controlId}</code>
                          <div className="np-choice-label mt-1">{c.title}</div>
                          <p className="np-choice-desc">{c.description || ''}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {selectedSourceControl && selectedTargetControl ? (
            <div className="col-12">
              <div className="np-card">
                <div className="np-card-body">
                  <h6 className="mb-3">Create Control Mapping</h6>
                  <div className="row g-3 align-items-end">
                    <div className="col-12 col-md-4">
                      <div className="np-hint mb-1">Source</div>
                      <div className="fw-medium">
                        <code>{selectedSourceControl.controlId}</code> - {selectedSourceControl.title}
                      </div>
                    </div>
                    <div className="col-12 col-md-4">
                      <div className="np-hint mb-1">Target</div>
                      <div className="fw-medium">
                        <code>{selectedTargetControl.controlId}</code> - {selectedTargetControl.title}
                      </div>
                    </div>
                    <div className="col-12 col-md-2">
                      <div className="np-field mb-0">
                        <label>Type</label>
                        <select className="np-select" value={mappingType} onChange={(e) => setMappingType(e.target.value as any)}>
                          <option value="equivalent">equivalent</option>
                          <option value="partial">partial</option>
                          <option value="related">related</option>
                        </select>
                      </div>
                    </div>
                    <div className="col-12 col-md-2">
                      <div className="np-field mb-0">
                        <label>Notes</label>
                        <input className="np-input" value={notes} onChange={(e) => setNotes(e.target.value)} />
                      </div>
                    </div>
                  </div>
                  <div className="mt-3">
                    <button className="np-btn np-btn-primary" onClick={addMapping}>
                      Create Mapping
                    </button>
                    <button
                      className="np-btn np-btn-ghost ms-2"
                      onClick={() => {
                        setSelectedTargetControlId(null);
                        setMappingType('equivalent');
                        setNotes('');
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          <div className="col-12">
            <div className="np-card">
              <div className="np-card-head">
                <h2>Current Mappings</h2>
              </div>
              <div className="np-card-body">
                {loadMappingsMutation.isPending ? (
                  <div className="np-sub">Loading mappings…</div>
                ) : currentMappings.length === 0 ? (
                  <div className="np-sub"><i className="ri-node-tree me-1"></i>No mappings created yet</div>
                ) : (
                  <NimbusTable thead={<tr>
                      <th>Source Control</th>
                      <th style={{ width: '150px' }}>Mapping Type</th>
                      <th>Target Control</th>
                      <th style={{ width: '90px' }}>Actions</th>
                    </tr>}>
                    {pageMappings.map((m, pageIdx) => {
                      const idx = (mappingsPage - 1) * mappingsPageSize + pageIdx;
                      return (
                        <tr key={`${m.sourceControlId}-${m.targetControlId}-${idx}`}
                        >
                          <td>
                            <code>{m.sourceControlId}</code>
                            <div className="np-hint">{m.sourceControlTitle || ''}</div>
                          </td>
                          <td>
                            <span className="np-chip np-tone-primary">{m.mappingType}</span>
                          </td>
                          <td>
                            <code>{m.targetControlId}</code>
                            <div className="np-hint">{m.targetControlTitle || ''}</div>
                          </td>
                          <td>
                            <button className="np-btn np-btn-ghost-danger np-btn-sm" onClick={() => removeMapping(idx)}>
                              Remove
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </NimbusTable>
                )}
                {pageMappings.length > 0 && (
                  <Pagination page={mappingsPage} totalPages={mappingsTotalPages} pageSize={mappingsPageSize} totalItems={mappingsTotalItems} onPageChange={setMappingsPage} />
                )}
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};
